/**
 * HomePage — /dashboard for every role except patients, who have their own
 * (PatientHomePage). The same calm, centered start screen: a greeting and the role's
 * apps as cards, in the sidebar's sections when the role has them. The apps come from the Apps launcher (useRoleApps), so Home, the
 * launcher and the sidebar all list the same things.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";

import { useAuth } from "hooks/useAuth";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { BrandGlowLayer } from "components/common/BrandGlow";
import {
  groupApps,
  useRoleApps,
} from "components/dashboard/layout/AppSwitcher";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apps = useRoleApps();
  // First visit is the sign-in that created the account (as on PatientHomePage).
  const firstVisit =
    user?.metadata?.creationTime &&
    user.metadata.creationTime === user.metadata.lastSignInTime;

  return (
    <Box
      sx={{
        // Not positioned, so BrandGlowLayer fills the whole <main>, not this column;
        // isolation keeps it behind the cards but above the page.
        isolation: "isolate",
        maxWidth: 760,
        mx: "auto",
        // Centered in the viewport; the offsets are the dashboard page column's
        // padding and, on phones, the top app bar.
        minHeight: "calc(100dvh - 128px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        py: 3,
      }}
    >
      <BrandGlowLayer preset="home" />
      {/* Same size as the other page titles (DashboardPageHeader). */}
      <Typography
        variant="h4"
        component="h1"
        textAlign="center"
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        {firstVisit ? "Welcome" : "Welcome back"}
      </Typography>
      <Typography
        color="text.secondary"
        textAlign="center"
        sx={{ mt: 1, mb: { xs: 3, md: 4 } }}
      >
        What would you like to do today?
      </Typography>

      {groupApps(apps).map((section, index) => (
        // Every section after the first is spaced off the one above, labelled or not;
        // an unlabelled one would otherwise sit flush under the last row of cards.
        <Box
          key={section.title || "apps"}
          sx={{ mt: index === 0 ? 0 : { xs: 3, md: 4 } }}
        >
          {/* Section labels as on PatientHomePage's "Your records". */}
          {section.title && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {section.title}
            </Typography>
          )}
          <Grid container spacing={2}>
            {section.apps.map((app) => (
              <Grid item xs={12} sm={6} key={app.path}>
                <SelectableChoiceCard
                  icon={app.icon}
                  title={app.name}
                  description={app.description}
                  onClick={() => navigate(app.path)}
                  alignIcon="center"
                  sx={{ py: 2.5 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default HomePage;
